import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Incident, IncidentComment, IncidentLinkedItem, IncidentKanbanLink } from '../types/incident';
import { Evidence } from '../types/player';
import { incidentsApiService } from '../services/incidentsApi';
import { MarkdownRenderer } from '../components/MarkdownRenderer';
import { LinkUserModal } from '../components/LinkUserModal';
import { LinkExistingEvidenceModal } from '../components/LinkExistingEvidenceModal';
import { ConfidentialEvidencePlaceholder } from '../components/ConfidentialEvidencePlaceholder';
import { ConfidentialBadge } from '../components/ConfidentialBadge';
import { isEvidenceRedacted, getEvidenceViewUrl } from '../utils/evidence';
import {
  AlertCircle,
  Edit,
  Trash2,
  ArrowLeft,
  MessageSquare,
  Loader,
  FileText,
  TriangleAlert,
  Axe,
  Users,
  Plus,
  Unlink,
  Paperclip,
  Upload,
  File,
  Eye,
  X,
  Lock,
  Clock,
  Info,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '../utils/cn';
import { useAuth } from '../contexts/AuthContext';
import { CreatorProfile } from '../components/CreatorProfile';
import { PlayerProfile } from '../components/PlayerProfile';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { EditIncidentModal } from '../components/EditIncidentModal';
import { motion } from 'framer-motion';
import MarkdownEditor from '../components/MarkdownEditor';
import { useIsMobile } from '../hooks/useIsMobile';
import { IncidentWorkspacesSection } from '../components/incidents/IncidentWorkspacesSection';
import { IncidentAssigneesSection } from '../components/incidents/IncidentAssigneesSection';
import { IncidentWatcherButton } from '../components/incidents/IncidentWatcherButton';
import { IncidentTimelineSection } from '../components/incidents/IncidentTimelineSection';
import { useIncidentWebSocket } from '../hooks/useIncidentRealtime';
import { shouldUseSyncForIncident } from '../utils/incidentSyncPolicy';
import type { IncidentWsEnvelope } from '../types/incidentRealtime';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

const STATUS_BADGE: Record<string, string> = {
  critical: 'bg-red-500/15 text-red-300 border-red-500/25',
  active: 'bg-blue-500/15 text-blue-300 border-blue-500/25',
  resolved: 'bg-green-500/15 text-green-300 border-green-500/25',
  closed: 'bg-gray-500/15 text-gray-300 border-gray-500/25',
  pending: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/25',
  investigating: 'bg-purple-500/15 text-purple-300 border-purple-500/25',
};

const getPriorityBadge = (priority: number) => {
  if (priority <= 2) return 'bg-red-500/15 text-red-300 border-red-500/25';
  if (priority <= 4) return 'bg-orange-500/15 text-orange-300 border-orange-500/25';
  if (priority <= 6) return 'bg-yellow-500/15 text-yellow-300 border-yellow-500/25';
  if (priority <= 8) return 'bg-blue-500/15 text-blue-300 border-blue-500/25';
  return 'bg-gray-500/15 text-gray-300 border-gray-500/25';
};

export const IncidentDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const [incident, setIncident] = useState<Incident | null>(null);
  const [comments, setComments] = useState<IncidentComment[]>([]);
  const [linkedItems, setLinkedItems] = useState<IncidentLinkedItem[]>([]);
  const [linkedUsers, setLinkedUsers] = useState<{ user_uuid: string; linked_by: string; linked_at: number }[]>([]);
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUserLinkModalOpen, setIsUserLinkModalOpen] = useState(false);
  const [isLinkEvidenceOpen, setIsLinkEvidenceOpen] = useState(false);
  const [isUploadingEvidence, setIsUploadingEvidence] = useState(false);
  const [uploadConfidential, setUploadConfidential] = useState(false);
  const [selectedImage, setSelectedImage] = useState<Evidence | null>(null);
  const isMobile = useIsMobile();

  // ── Bridge / sync / realtime state ────────────────────────────────────
  const [kanbanLinks, setKanbanLinks] = useState<IncidentKanbanLink[]>([]);
  const [workspacesNonce, setWorkspacesNonce] = useState(0);
  const [assigneesNonce, setAssigneesNonce] = useState(0);
  const [watchersNonce, setWatchersNonce] = useState(0);
  const [timelineNonce, setTimelineNonce] = useState(0);
  const [activeBottomTab, setActiveBottomTab] = useState<'timeline' | 'comments'>('timeline');

  /**
   * Decide which `*-with-sync` companion endpoints to call. Whenever the local
   * `kanbanLinks` snapshot has at least one bridge with at least one sync flag on,
   * we route mutations through the with-sync variant. Otherwise we use the legacy
   * endpoint (zero side effects), exactly per the integration guide.
   */
  const useSync = useMemo(() => shouldUseSyncForIncident(kanbanLinks), [kanbanLinks]);

  useDocumentTitle(incident ? `Incident: ${incident.title}` : 'Incident Details');

  const canManage = useMemo(() => {
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('SPY_Notes') || groups.includes('SPY_Notes_Editor');
  }, [identity]);

  const canDelete = useMemo(() => {
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('SPY_Super_admin');
  }, [identity]);

  const fetchAllData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);

    try {
      const [incidentData, commentsData, itemsData, usersData, evidenceData, linksData] =
        await Promise.all([
          incidentsApiService.getIncident(id),
          incidentsApiService.getComments(id),
          incidentsApiService.getItemsForIncident(id),
          incidentsApiService.getUsersForIncident(id),
          incidentsApiService.getEvidenceForIncident(id),
          incidentsApiService.listKanbanLinks(id).catch(() => [] as IncidentKanbanLink[]),
        ]);

      setIncident(incidentData);
      setComments(commentsData);
      setLinkedItems(itemsData);
      setLinkedUsers(usersData);
      setEvidence(evidenceData);
      setKanbanLinks(linksData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load incident');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchAllData();
  }, [fetchAllData]);

  // ── Realtime: subscribe to `incident:{id}` and dispatch by event type ──
  const handleIncidentWs = useCallback(
    (env: IncidentWsEnvelope) => {
      if (env.type.startsWith('system.')) return;
      const p = env.payload ?? {};
      switch (env.type) {
        case 'incident.updated': {
          if (p.after && typeof p.after === 'object') {
            setIncident(p.after as Incident);
          }
          setTimelineNonce((n) => n + 1);
          return;
        }
        case 'incident.commented': {
          if (p.comment) {
            const c = p.comment as IncidentComment;
            setComments((prev) =>
              prev.some((existing) => existing.id === c.id) ? prev : [...prev, c],
            );
          }
          setTimelineNonce((n) => n + 1);
          return;
        }
        case 'incident.linked':
        case 'incident.unlinked': {
          setWorkspacesNonce((n) => n + 1);
          setTimelineNonce((n) => n + 1);
          // Refetch the local link snapshot so `useSync` re-evaluates.
          if (id) {
            void incidentsApiService
              .listKanbanLinks(id)
              .then(setKanbanLinks)
              .catch(() => {
                /* keep stale snapshot */
              });
          }
          return;
        }
        case 'incident.assignee_changed': {
          setAssigneesNonce((n) => n + 1);
          setTimelineNonce((n) => n + 1);
          return;
        }
        case 'incident.watcher_changed': {
          setWatchersNonce((n) => n + 1);
          setTimelineNonce((n) => n + 1);
          return;
        }
        case 'incident.status_changed':
        case 'incident.priority_changed': {
          // Already covered by `incident.updated`; just refresh the timeline.
          setTimelineNonce((n) => n + 1);
          return;
        }
        default:
          // Unknown event — best-effort timeline refresh.
          setTimelineNonce((n) => n + 1);
      }
    },
    [id],
  );

  const handleSeqGap = useCallback(() => {
    void fetchAllData();
    setWorkspacesNonce((n) => n + 1);
    setAssigneesNonce((n) => n + 1);
    setWatchersNonce((n) => n + 1);
    setTimelineNonce((n) => n + 1);
  }, [fetchAllData]);

  useIncidentWebSocket({
    incidentId: id,
    enabled: !!id && !!identity,
    onEnvelope: handleIncidentWs,
    onSeqGap: handleSeqGap,
  });

  const handleAddComment = async () => {
    if (!newComment.trim() || !id) return;

    setIsSubmittingComment(true);
    try {
      const comment = useSync
        ? await incidentsApiService.addCommentWithSync(id, newComment)
        : await incidentsApiService.addComment(id, newComment);
      setComments(prev => [...prev, comment]);
      setNewComment('');
    } catch (err) {
      alert('Failed to add comment');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    
    setIsDeleting(true);
    try {
      await incidentsApiService.deleteIncident(id);
      navigate('/incidents');
    } catch (err) {
      alert('Failed to delete incident');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleUnlinkUser = async (userUuid: string) => {
    if (!id) return;
    
    try {
      await incidentsApiService.unlinkUser(id, userUuid);
      setLinkedUsers(prev => prev.filter(u => u.user_uuid !== userUuid));
    } catch (err) {
      alert('Failed to unlink user');
    }
  };

  const handleUploadEvidence = async (file: File) => {
    if (!id) return;
    
    setIsUploadingEvidence(true);
    try {
      const uploadedEvidence = await incidentsApiService.uploadEvidence(id, file, uploadConfidential || undefined);
      setEvidence(prev => [...prev, uploadedEvidence]);
    } catch (err) {
      alert('Failed to upload evidence');
    } finally {
      setIsUploadingEvidence(false);
    }
  };

  const handleLinkExistingEvidence = async (evidenceId: string) => {
    if (!id) return;
    
    await incidentsApiService.linkEvidence(id, evidenceId);
    const updatedEvidence = await incidentsApiService.getEvidenceForIncident(id);
    setEvidence(updatedEvidence);
  };

  const handleUnlinkEvidence = async (evidenceId: string) => {
    if (!id) return;
    
    try {
      await incidentsApiService.unlinkEvidence(id, evidenceId);
      setEvidence(prev => prev.filter(e => e.id !== evidenceId));
    } catch (err) {
      alert('Failed to remove evidence');
    }
  };

  const handleViewEvidence = (item: Evidence) => {
    if (isEvidenceRedacted(item, identity)) return;
    if (item.content_type.startsWith('image/')) {
      setSelectedImage(item);
    } else {
      window.open(getEvidenceViewUrl(item.id), '_blank');
    }
  };

  const getItemIcon = (itemType: string) => {
    switch (itemType) {
      case 'note': return <FileText className="w-4 h-4 text-blue-400" />;
      case 'warning': return <TriangleAlert className="w-4 h-4 text-yellow-400" />;
      case 'ban': return <Axe className="w-4 h-4 text-red-400" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" aria-busy="true">
        <div className="space-y-3">
          <SkeletonLoader className="h-3 w-20" />
          <div className="flex items-center gap-3">
            <SkeletonLoader className="h-7 w-72 max-w-full" />
            <SkeletonLoader className="h-5 w-16 rounded-full" />
            <SkeletonLoader className="h-5 w-10 rounded-full" />
          </div>
          <SkeletonLoader className="h-4 w-96 max-w-full" />
        </div>
        {[0, 1, 2].map((i) => (
          <div key={i} className="glass-card p-4 md:p-5 space-y-3">
            <SkeletonLoader className="h-4 w-32" />
            <SkeletonLoader className="h-3 w-full" />
            <SkeletonLoader className="h-3 w-5/6" />
          </div>
        ))}
      </div>
    );
  }

  if (error || !incident) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Moderation" title="Incident" backTo="/incidents" backLabel="Incidents" />
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load incident"
          description={error || 'Incident not found'}
          action={
            <>
              <button onClick={() => void fetchAllData()} className="glass-button">Retry</button>
              <button onClick={() => navigate('/incidents')} className="glass-button-secondary">
                <ArrowLeft className="w-4 h-4" />
                Back to Incidents
              </button>
            </>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        eyebrow="Moderation"
        backTo="/incidents"
        backLabel="Incidents"
        title={incident.title}
        titleAdornment={
          <>
            <span className={cn("badge badge-dot", STATUS_BADGE[incident.status] ?? 'badge-neutral')}>
              {incident.status.charAt(0).toUpperCase() + incident.status.slice(1)}
            </span>
            <span className={cn("badge", getPriorityBadge(incident.priority))} title={`Priority ${incident.priority}`}>
              P{incident.priority}
            </span>
          </>
        }
        description={
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 tabular-nums">
            <span>ID: <span className="font-mono text-xs">{incident.id}</span></span>
            <span>Created {formatDistanceToNow(new Date(incident.created_at), { addSuffix: true })}</span>
            <span>By <CreatorProfile creatorId={incident.creator_id} inline /></span>
            {incident.updated_at && (
              <span>Updated {formatDistanceToNow(new Date(incident.updated_at), { addSuffix: true })}</span>
            )}
          </span>
        }
        actions={
          <>
            {id && (
              <IncidentWatcherButton
                incidentId={id}
                refreshNonce={watchersNonce}
              />
            )}
            {canManage && (
              <button onClick={() => setIsEditing(true)} className="glass-button">
                <Edit className="w-4 h-4" />
                Edit
              </button>
            )}
            {canDelete && (
              <button onClick={() => setShowDeleteConfirm(true)} className="btn-danger">
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}
          </>
        }
      />

      {/* Description */}
      <div className="glass-card p-4 md:p-5">
        <SectionHeader as="h2" title="Description" />
        <MarkdownRenderer content={incident.description} />
      </div>

      {/* Workspaces (kanban bridge links + sync flag toggles + promote-to-card) */}
      {id && (
        <IncidentWorkspacesSection
          incidentId={id}
          canManage={canManage}
          refreshNonce={workspacesNonce}
          onLinksChanged={setKanbanLinks}
        />
      )}

      {/* Staff Assignees */}
      {id && (
        <IncidentAssigneesSection
          incidentId={id}
          canManage={canManage}
          useSync={useSync}
          refreshNonce={assigneesNonce}
        />
      )}

      {/* Linked Items */}
      {linkedItems.length > 0 && (
        <div className="glass-card p-4 md:p-5">
          <SectionHeader as="h2" title="Linked Items" icon={FileText} count={linkedItems.length} />
          <div className="divide-y divide-glass-200/10">
            {linkedItems.map((item) => (
              <div key={item.item_id} className="flex items-center gap-3 py-2.5">
                {getItemIcon(item.item_type)}
                <div className="flex-1 min-w-0">
                  <span className="text-white text-sm font-medium capitalize">{item.item_type}</span>
                  <span className="text-glass-400 text-xs ml-2 font-mono">{item.item_id}</span>
                </div>
                <span className="text-xs text-glass-400 tabular-nums whitespace-nowrap">
                  Linked {formatDistanceToNow(new Date(item.linked_at), { addSuffix: true })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Evidence */}
      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          as="h2"
          title="Evidence"
          icon={Paperclip}
          count={evidence.length}
          actions={canManage ? (
            <>
              <button
                onClick={() => setIsLinkEvidenceOpen(true)}
                className="glass-button-sm"
              >
                <Paperclip className="w-3.5 h-3.5" />
                Link Existing
              </button>
              <button
                type="button"
                onClick={() => setUploadConfidential(!uploadConfidential)}
                className={cn(
                  "btn-icon",
                  uploadConfidential && "bg-amber-500/15 text-amber-300 border-amber-500/25"
                )}
                title={uploadConfidential ? 'Uploads will be confidential' : 'Uploads will be public'}
                aria-label={uploadConfidential ? 'Uploads will be confidential' : 'Uploads will be public'}
                aria-pressed={uploadConfidential}
              >
                <Lock className="w-4 h-4" />
              </button>
              <label className="glass-button-sm cursor-pointer">
                <Upload className="w-3.5 h-3.5" />
                Upload
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUploadEvidence(file);
                  }}
                  disabled={isUploadingEvidence}
                  accept="image/*,video/*,.pdf,.txt,.doc,.docx"
                />
              </label>
            </>
          ) : undefined}
        />
        {isUploadingEvidence && (
          <div className="flex items-center gap-2 mb-3 text-xs text-glass-400">
            <Loader className="w-4 h-4 animate-spin" />
            <span>Uploading…</span>
          </div>
        )}
        {evidence.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {evidence.map((item) => {
              const redacted = isEvidenceRedacted(item, identity);
              return (
              <div key={item.id} className="relative group">
                <div className="aspect-square bg-dark-800 rounded-md overflow-hidden">
                  {redacted ? (
                    <ConfidentialEvidencePlaceholder />
                  ) : item.content_type.startsWith('image/') ? (
                    <img
                      src={getEvidenceViewUrl(item.id)}
                      alt={item.filename}
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-glass-400 p-2">
                      <File className="w-8 h-8" />
                      <span className="text-xs text-center font-mono truncate mt-2">{item.filename}</span>
                    </div>
                  )}
                  {!redacted && (
                    <div className={cn(
                      "absolute inset-0 bg-black/70 transition-opacity flex items-center justify-center gap-2 rounded-md",
                      isMobile ? "opacity-100 bg-black/40" : "opacity-0 group-hover:opacity-100"
                    )}>
                      <button
                        onClick={() => handleViewEvidence(item)}
                        className="p-2 rounded-full bg-glass-200/20 hover:bg-blue-500/50 text-white"
                        title="View"
                        aria-label="View evidence"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      {canManage && (
                        <button
                          onClick={() => handleUnlinkEvidence(item.id)}
                          className="p-2 rounded-full bg-glass-200/20 hover:bg-red-500/50 text-white"
                          title="Remove"
                          aria-label="Remove evidence"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <p className="text-xs text-white truncate flex-1" title={item.filename}>{item.filename}</p>
                  {item.confidential && !redacted && <ConfidentialBadge />}
                </div>
                <p className="text-xs text-glass-400">
                  <CreatorProfile creatorId={item.uploader_id} inline />
                </p>
              </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-glass-400">No evidence attached to this incident.</p>
        )}
      </div>

      {/* Linked Users — INVOLVED PLAYERS (suspects / victims / reporters); distinct from Staff Assignees above. */}
      <div className="glass-card p-4 md:p-5">
        <SectionHeader
          as="h2"
          title={
            <span className="inline-flex items-center gap-1.5">
              Involved Players
              <span
                className="text-glass-500 cursor-help inline-flex"
                title="Minecraft players involved in the incident — suspects, victims, reporters. Different from the Staff Assignees section above."
              >
                <Info className="w-3.5 h-3.5" />
              </span>
            </span>
          }
          icon={Users}
          count={linkedUsers.length}
          actions={canManage ? (
            <button onClick={() => setIsUserLinkModalOpen(true)} className="glass-button-sm">
              <Plus className="w-3.5 h-3.5" />
              Link User
            </button>
          ) : undefined}
        />
        {linkedUsers.length > 0 ? (
          <div className="divide-y divide-glass-200/10">
            {linkedUsers.map((user) => (
              <div key={user.user_uuid} className="flex items-center justify-between gap-3 py-2.5">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Users className="w-4 h-4 text-purple-400 shrink-0" />
                  <PlayerProfile uuid={user.user_uuid} showUuid={true} />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-glass-400 tabular-nums whitespace-nowrap">
                    Linked {formatDistanceToNow(new Date(user.linked_at), { addSuffix: true })}
                  </span>
                  {canManage && (
                    <button
                      onClick={() => handleUnlinkUser(user.user_uuid)}
                      className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                      title="Unlink user"
                      aria-label="Unlink user"
                    >
                      <Unlink className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-glass-400">No users linked to this incident.</p>
        )}
      </div>

      {/* Comment composer (always visible; submission lands in the Comments tab below). */}
      {canManage && (
        <div className="glass-card p-4 md:p-5">
          <SectionHeader
            as="h2"
            title="Add a comment"
            icon={MessageSquare}
            actions={useSync ? (
              <span
                className="badge badge-purple"
                title="At least one bridge link has a sync flag on. The comment will use the with-sync endpoint, mirroring it to linked kanban cards (when sync_comments is on)."
              >
                Sync on
              </span>
            ) : undefined}
          />
          <MarkdownEditor
            value={newComment}
            onChange={setNewComment}
            height={200}
            placeholder="Enter comment in markdown..."
          />
          <button
            onClick={handleAddComment}
            disabled={!newComment.trim() || isSubmittingComment}
            className="btn-primary mt-3"
          >
            {isSubmittingComment ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Posting...
              </>
            ) : (
              'Post Comment'
            )}
          </button>
        </div>
      )}

      {/* Tabbed activity feed: Timeline (merged) ⇄ Comments (incident only) */}
      <div
        role="tablist"
        aria-label="Incident activity"
        className="tabs"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeBottomTab === 'timeline'}
          data-active={activeBottomTab === 'timeline'}
          onClick={() => setActiveBottomTab('timeline')}
          className="tab"
        >
          <Clock className="w-4 h-4" />
          Timeline
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeBottomTab === 'comments'}
          data-active={activeBottomTab === 'comments'}
          onClick={() => setActiveBottomTab('comments')}
          className="tab"
        >
          <MessageSquare className="w-4 h-4" />
          Comments
          <span className="badge badge-neutral">{comments.length}</span>
        </button>
      </div>

      {activeBottomTab === 'timeline' ? (
        id ? (
          <IncidentTimelineSection incidentId={id} refreshNonce={timelineNonce} />
        ) : null
      ) : (
        <div className="glass-card p-4 md:p-5">
          <SectionHeader as="h2" title="Comments" icon={MessageSquare} count={comments.length} />
          <div className="divide-y divide-glass-200/10">
            {comments.length === 0 ? (
              <p className="text-glass-400 text-sm">No comments yet.</p>
            ) : (
              [...comments]
                .sort((a, b) => b.created_at - a.created_at)
                .map((comment) => (
                  <motion.div
                    key={comment.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="py-3 first:pt-0 last:pb-0"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <CreatorProfile creatorId={comment.author_id} inline />
                        <span className="text-xs text-glass-400 tabular-nums">
                          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                    <MarkdownRenderer content={comment.content} />
                  </motion.div>
                ))
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <ConfirmationModal
          title="Delete Incident"
          message="Are you sure you want to delete this incident? This action cannot be undone and will delete all associated comments and links."
          confirmText="Delete"
          onConfirm={handleDelete}
          onClose={() => setShowDeleteConfirm(false)}
          isLoading={isDeleting}
        />
      )}

      {/* Edit Modal */}
      {isEditing && incident && (
        <EditIncidentModal
          incident={incident}
          useSync={useSync}
          onClose={() => setIsEditing(false)}
          onSave={(updatedIncident) => {
            setIncident(updatedIncident);
            setIsEditing(false);
            setTimelineNonce((n) => n + 1);
          }}
        />
      )}

      {/* User Link Modal */}
      {isUserLinkModalOpen && id && (
        <LinkUserModal
          isOpen={isUserLinkModalOpen}
          onClose={() => setIsUserLinkModalOpen(false)}
          onSuccess={() => {
            // Refresh data to show new linked user
            if (id) {
              incidentsApiService.getUsersForIncident(id).then(setLinkedUsers);
            }
          }}
          incidentId={id}
        />
      )}

      {/* Link Existing Evidence Modal */}
      {isLinkEvidenceOpen && id && (
        <LinkExistingEvidenceModal
          isOpen={isLinkEvidenceOpen}
          onClose={() => setIsLinkEvidenceOpen(false)}
          onLink={handleLinkExistingEvidence}
          excludeEvidenceIds={evidence.map(e => e.id)}
        />
      )}

      {/* Image Lightbox */}
      {selectedImage && !isEvidenceRedacted(selectedImage, identity) && (
        <div className="modal-overlay z-50" onClick={() => setSelectedImage(null)}>
          <img
            src={getEvidenceViewUrl(selectedImage.id)}
            alt={selectedImage.filename}
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />
          <button className="absolute top-4 right-4 text-white hover:text-gray-300" onClick={() => setSelectedImage(null)} title="Close" aria-label="Close">
            <X className="w-8 h-8" />
          </button>
        </div>
      )}
    </div>
  );
};
